import React, { useState } from 'react';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { User, Mail, Lock, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user, login } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    if (!email.trim()) {
      toast.error('Email cannot be empty');
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), email: email.trim() };
      const res = await axios.put(`${API}/users/${user.id}`, payload);
      login(res.data);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error('Enter your current password');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/users/${user.id}`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <OwnerLayout>
      <div className="max-w-2xl mx-auto">
        <h1 className="font-heading text-3xl font-bold text-gray-800 mb-2" data-testid="settings-title">
          Settings
        </h1>
        <p className="text-gray-500 font-body mb-8">Manage your account details.</p>

        {/* Profile Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="font-heading text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <User className="w-5 h-5 text-edupaws-sage" />
            Profile
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="font-body font-bold text-sm text-gray-700">Name</Label>
              <Input
                data-testid="settings-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 font-body"
                placeholder="Your name"
              />
            </div>
            <div>
              <Label className="font-body font-bold text-sm text-gray-700">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  data-testid="settings-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 font-body"
                  placeholder="your@email.com"
                  type="email"
                />
              </div>
            </div>
            <button
              data-testid="settings-save-profile-btn"
              onClick={handleSaveProfile}
              disabled={saving}
              className="flex items-center gap-2 bg-edupaws-sage text-white font-body font-bold py-2.5 px-6 rounded-full
                         hover:bg-edupaws-sage/90 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </div>

        {/* Password Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="font-heading text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <Lock className="w-5 h-5 text-edupaws-sage" />
            Change Password
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="font-body font-bold text-sm text-gray-700">Current Password</Label>
              <div className="relative mt-1">
                <Input
                  data-testid="settings-current-password"
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="pr-10 font-body"
                  placeholder="Enter current password"
                />
                <button onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="font-body font-bold text-sm text-gray-700">New Password</Label>
              <div className="relative mt-1">
                <Input
                  data-testid="settings-new-password"
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pr-10 font-body"
                  placeholder="At least 6 characters"
                />
                <button onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="font-body font-bold text-sm text-gray-700">Confirm New Password</Label>
              <Input
                data-testid="settings-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 font-body"
                placeholder="Re-enter new password"
              />
            </div>
            <button
              data-testid="settings-change-password-btn"
              onClick={handleChangePassword}
              disabled={saving}
              className="flex items-center gap-2 bg-edupaws-navy text-white font-body font-bold py-2.5 px-6 rounded-full
                         hover:bg-edupaws-navy/90 transition-colors disabled:opacity-50"
            >
              <Lock className="w-4 h-4" />
              {saving ? 'Saving...' : 'Change Password'}
            </button>
          </div>
        </div>
      </div>
    </OwnerLayout>
  );
}
